import React, { useContext } from 'react';

import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
// En React Router 7 el paquete @remix-run/router desaparecio: se fusiono dentro
// de react-router, y el tipo del router de datos se exporta como `DataRouter`.
import type { DataRouter } from 'react-router';
import * as Sentry from '@sentry/react';

// Layouts
import AuthLayout from '../components/layouts/AuthLayout';
import MainLayout from '../components/layouts/MainLayout';

// Splash de arranque (evita el blanco puro mientras el router resuelve los loaders iniciales)
import { SplashScreen } from '../components/display/SplashScreen';
import CommunicationsLayout from '../pages/main/comunications/CommunicationsLayout';
import WorkOrdersLayout from '../pages/main/workOrders/WorkOrdersLayout';
import LayoutResetIndex from '../pages/main/comunications/components/LayoutResetIndex';

// Pages Main
//import DashboardPage from '../pages/main/Dashboard';

import NewCommunicationPage from '../pages/main/comunications/NewComunication';
import NewWorkOrderSummary from '../pages/main/workOrders/NewOrder/WorkOrderSummary';
import NewCommunicationSummary from '../pages/main/comunications/NewComunication/CommunicationSummary';
import EditCommunicationSummaryPage from '../pages/main/workOrders/EditCommunication/CommunicationSummary';
import DuplicateCommunicationPage from '../pages/main/comunications/DuplicateComunication';
import NewCommunicationDuplicatePage from '../pages/main/comunications/DuplicateComunication/NewComunication';
import NewCommunicationDuplicateSummaryPage from '../pages/main/comunications/DuplicateComunication/CommunicationSummary';
import HistoricalCommunicationsReportPage from '../pages/main/reporting/HistoricalCommunicationsReport';
import GeneralViewPage from '../pages/main/workOrders/GeneralView';
import NewOrderPage from '../pages/main/workOrders/NewOrder';
import DuplicateOrderPage from '../pages/main/workOrders/DuplicateOrder';
import SearchAndCreateHistoricalOrderPage from '../pages/main/workOrders/SearchAndCreateHistoricalOrder';
import SearchAndCreateHistoricalActionPage from '../pages/main/workOrders/SearchAndCreateHistoricalAction';
import CampaignsPage from '../pages/main/workOrders/Campaigns';
import SearchAndCreateHistoricalCampaignPage from '../pages/main/workOrders/SearchAndCreateHistoricalCampaign';
import NewCampaignPage from '../pages/main/workOrders/NewCampaign';
import ActionsValidation from '../pages/main/workOrders/ActionsValidation';
import SurveillanceRouteLayout from '../pages/main/surveillanceRoute/SurveillanceRouteLayout';
// Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — wrapper de pestañas del
// LISTADO del módulo RV (Vista general/Actuacions/Multimedia) + los 2
// bindings contextuales (filtran por `containerTypes: ['surveillanceRoute']`,
// sin `:id` de la URL). Reemplaza el diseño equivocado de 8e433e04
// (revertido), que ponía estas 2 pestañas en el detalle de cada RV — el
// mismo criterio por el que el detalle de campaña tampoco las tiene.
import GeneralViewRoutePage, {
  RouteActionsTab,
  RouteMultimediaTab,
} from '../pages/main/surveillanceRoute/GeneralViewRoute';
import { SurveillanceRoutesPage } from '../pages/main/surveillanceRoute/GeneralViewRoute/SurveillanceRoutesPage';
import NewRoutePage from '../pages/main/surveillanceRoute/NewRoute';
import SearchAndCreateHistoricalRoutePage from '../pages/main/surveillanceRoute/SearchAndCreateHistoricalRoute';
import { LoaderNewSurveillanceRoute } from '../pages/main/surveillanceRoute/NewRoute/loader';
import { LoaderSearchAndCreateHistoricalRoute } from '../pages/main/surveillanceRoute/SearchAndCreateHistoricalRoute/loader';
import { LoaderRouteDetailed } from '../pages/main/surveillanceRoute/RouteDetailed/loader';
import RouteDetailedPage from '../pages/main/surveillanceRoute/RouteDetailed';
import RoutePreviewPage from '../pages/main/surveillanceRoute/RouteDetailed/RoutePreviewPage';
import NewSurveillanceRouteOrderPage from '../pages/main/surveillanceRoute/NewSurveillanceRouteOrder';
import SurveillanceRouteOrderSummary from '../pages/main/surveillanceRoute/NewSurveillanceRouteOrder/Summary';
import { ActionNewSurveillanceRouteOrderSummary } from '../pages/main/surveillanceRoute/NewSurveillanceRouteOrder/Summary/action';
import { ActionNewRoute } from '../pages/main/surveillanceRoute/NewRoute/action';
import EditRoutePage from '../pages/main/surveillanceRoute/EditRoute';
import { LoaderEditRoute } from '../pages/main/surveillanceRoute/EditRoute/loader';
import { ActionEditRoute } from '../pages/main/surveillanceRoute/EditRoute/action';
import RouteMultimediaPage from '../pages/main/surveillanceRoute/RouteDetailed/MultimediaPage';
import { LoaderRouteMultimedia } from '../pages/main/surveillanceRoute/RouteDetailed/MultimediaPage/loader';
import RouteChildOrdersPage from '../pages/main/surveillanceRoute/RouteDetailed/ChildOrdersPage';
import { LoaderRouteChildOrders } from '../pages/main/surveillanceRoute/RouteDetailed/ChildOrdersPage/loader';
import { LoaderSurveillanceRoutesGeneralView } from '../pages/main/surveillanceRoute/GeneralViewRoute/loader';
import ReportingLayout from '../pages/main/reporting/ReportingLayout';
import HistoricalActionsReportPage from '../pages/main/reporting/HistoricalActionsReport';
import HistoricalAccidentsReportPage from '../pages/main/reporting/HistoricalAccidentsReport';
import TimeManagementPage from '../pages/main/reporting/TimeManagement';
import ReportingIndexRedirect from '../pages/main/reporting/ReportingLayout/ReportingIndexRedirect';
import { HistoricalActionsReportLoader } from '../pages/main/reporting/HistoricalActionsReport/loader';
import { HistoricalAccidentsReportLoader } from '../pages/main/reporting/HistoricalAccidentsReport/loader';
import { TimeManagementLoader } from '../pages/main/reporting/TimeManagement/loader';
import WorkOrderDetailed from '../pages/main/workOrders/WorkOrderDetailed';
import NewOrderDuplicatePage from '../pages/main/workOrders/DuplicateOrder/NewOrderDuplicate';
import WorkOrderDuplicateSummaryPage from '../pages/main/workOrders/DuplicateOrder/WorkOrderSummary';
import EditCampaignPage from '../pages/main/workOrders/EditCampaign';
import CampaignSummary from '../pages/main/workOrders/NewCampaign/CampaignSummary';
import CampaignDetailed from '../pages/main/workOrders/CampaignDetailed';
// Pestañas de módulo del LISTADO de Campañas (`/work-orders/campaign`) —
// wrapper de pestañas + los 4 bindings contextuales con `containerTypes:
// ['campaign']` (filtro por TIPO de origen, sin `:id` — espejo EXACTO del
// patrón RV `GeneralViewRoute`, ver comentario en el propio módulo).
import GeneralViewCampaignPage, {
  CampaignListActionsTab,
  CampaignListAccidentsTab,
  CampaignListCommunicationsTab,
  CampaignListMultimediaTab,
} from '../pages/main/workOrders/Campaigns/GeneralViewCampaign';
import SearchAndCreateHistoryWorkOrderSummary from '../pages/main/workOrders/SearchAndCreateHistoricalOrder/WorkOrderSummary';
import SearchAndCreateHistoricalActionSummary from '../pages/main/workOrders/SearchAndCreateHistoricalAction/ActionSummary';
import EditCampaignSummaryPage from '../pages/main/workOrders/EditCampaign/CampaignSummary';
import AddBasicInfoAccidentPage from '../pages/main/workOrders/AddBasicInfoAccident';
import AddVehiclePage from '../pages/main/workOrders/AddVehicle';
import ActionPage from '../pages/main/workOrders/NewAction/ActionPage';
import AccidentSummaryGeneralView from '../pages/main/workOrders/GeneralView/AccidentsPage/AccidentPageSummary';
import AnimalsFormPage from '../pages/main/workOrders/NewAction/Animals';
import OperatorsAndVehiclesFormPage from '../pages/main/workOrders/NewAction/OperatorsAndVehicles';
import MaterialsFormPage from '../pages/main/workOrders/NewAction/Materials';
import MachineryFormPage from '../pages/main/workOrders/NewAction/Machinery';
import EditionActionFormPage from '../pages/main/workOrders/NewAction/Edition';
import LocationFormPage from '../pages/main/workOrders/NewAction/Location';
import ActionSummaryPage from '../pages/main/workOrders/NewAction/ActionSummary';
import NewActionPage from '../pages/main/workOrders/NewAction';
import BudgetItemsFormPage from '../pages/main/workOrders/NewAction/BudgetItems';
import OperatorsAndVehiclesFormValidationPage from '../pages/main/workOrders/ActionsValidation/OperatorsAndVehicles';
import BudgetItemsValidationFormPage from '../pages/main/workOrders/ActionsValidation/BudgetItems';
import NewAccidentPage from '../pages/main/workOrders/NewAccident';
import { WorkOrdersPage } from '../pages/main/workOrders/GeneralView/WorkOrdersPage';
import { AccidentsPage } from '../pages/main/workOrders/GeneralView/AccidentsPage';
import { CommunicationsPage } from '../pages/main/workOrders/GeneralView/CommunicationsPage';
import { MultimediaPage } from '../pages/main/workOrders/GeneralView/MultimediaPage';
import { ActionsPage } from '../pages/main/workOrders/GeneralView/ActionsPage';
import { CommunicationSummaryGeneralView } from '../pages/main/workOrders/GeneralView/CommunicationsPage/CommunicationSummary';
import { MultimediaGridGeneralView } from '../pages/main/workOrders/GeneralView/MultimediaPage/MultimediaGrid';
import ActionValidationPage from '../pages/main/workOrders/ActionsValidation/ActionPage';
import LocationActionFormPage from '../pages/main/workOrders/ActionsValidation/Location';
import EditionValidationFormPage from '../pages/main/workOrders/ActionsValidation/Edition';
import MaterialsValidationFormPage from '../pages/main/workOrders/ActionsValidation/Materials';
import MachineryValidationFormPage from '../pages/main/workOrders/ActionsValidation/Machinery';
import AnimalsValidationFormPage from '../pages/main/workOrders/ActionsValidation/Animals';
import UsersManagementPage from '../pages/main/management/users';
import VehiclesManagementPage from '../pages/main/management/vehicles';
import TeamsManagementPage from '../pages/main/management/teams';
import GroupsManagementPage from '../pages/main/management/groups';
import SegmentReviewSettingsPage from '../pages/main/management/segmentReview';
import ManagementRoutesLayout from '../pages/main/management/managementRouteLayout';
import MaterialsManagementPage from '../pages/main/management/materials';
import SearchAndCreateHistoricalBudgetPage from '../pages/main/budgets/SearchAndCreateHistoricalBudget/index';
import { SearchAndCreateHistoricalBudgetLoader } from '../pages/main/budgets/SearchAndCreateHistoricalBudget/loader';
import BudgetItemSummary from '../pages/main/budgets/SearchAndCreateHistoricalBudget/BudgetSummary';
import { LoaderBudgetItemSummary } from '../pages/main/budgets/SearchAndCreateHistoricalBudget/BudgetSummary/loader';
import EditAccidentWorkOrderPage from '../pages/main/workOrders/EditAccident/WorkOrder';
import EditBasicInfoAccidentPage from '../pages/main/workOrders/EditAccident/BasicInfo';
import EditCommunicationPage from '../pages/main/workOrders/EditCommunication';

// Pages Auth
import LoginPage from '../pages/auth/Login';
import ResetPasswordPage from '../pages/auth/ResetPassword';
import ForgotPasswordPage from '../pages/auth/ForgotPassword';

// Roles Auth Guard
import { RolesAuthRoute } from './RolesAuthRoute';

// Pantalla nueva por entidad: las rutas de entidad de un asistente con
// borradores (`:id`, `summary/:id`) van dentro de `<EntityScreen>` para que
// cambiar de entidad sin salir de la ruta (atrás/adelante) la monte de cero.
// Lo vigila el tripwire T1 de `tests/unit/drafts/` (spec 2026-11-02
// wizard-draft-ownership, R3/R4).
import { EntityScreen } from './EntityScreen';
// Crear actuación no lleva id en la URL (`new-action/details/…`): la entidad
// de su pantalla es el borrador en curso (`draftId` de `new-action-data`).
// Cambia cuando una puerta empieza otra creación — p. ej. el cronómetro crea
// la básica B estando en Ubicación de la A y redirige a la MISMA URL — y la
// pantalla se monta de cero (auditoría de creación 2.1, M2).
import { readCreationDraftId } from '../utils/draftScopes';

// Pages Error
import ErrorPage from '../pages/utils/Error';
import NotFoundPage from '../pages/utils/NotFound';

// Loaders
import { LoaderIndexPage } from './loaderIndexPage';
import { LoaderBaseDataMain } from '../components/layouts/MainLayout/loader';
import { NewCommunicationLoader } from '../pages/main/comunications/NewComunication/loader';
import { HistoricalCommunicationsLoader } from '../pages/main/reporting/HistoricalCommunicationsReport/loader';
import { LoaderCommunicationDuplicates } from '../pages/main/comunications/DuplicateComunication/loader';
import { NewCommunicationDuplicateLoader } from '../pages/main/comunications/DuplicateComunication/NewComunication/loader';
import { LoaderCommunicationDuplicateSummary } from '../pages/main/comunications/DuplicateComunication/CommunicationSummary/loader';
import { NewWorkOrderLoader } from '../pages/main/workOrders/NewOrder/loader';
import { LoaderWorkOrderDetailed } from '../pages/main/workOrders/WorkOrderDetailed/loader';
import { NewWorkOrderDuplicateLoader } from '../pages/main/workOrders/DuplicateOrder/NewOrderDuplicate/loader';
import { LoaderOrderDuplicates } from '../pages/main/workOrders/DuplicateOrder/loader';
import { LoaderOrderSummaryDuplicates } from '../pages/main/workOrders/DuplicateOrder/WorkOrderSummary/loader';
import { LoaderWorkOrdersGeneralView } from '../pages/main/workOrders/GeneralView/loader';
import { SearchAndCreateHistoricalWorkOrdersLoader } from '../pages/main/workOrders/SearchAndCreateHistoricalOrder/loader';
import { SearchAndCreateHistoricalActionLoader } from '../pages/main/workOrders/SearchAndCreateHistoricalAction/loader';
import { ActionSummaryLoader as HistoricalActionSummaryLoader } from '../pages/main/workOrders/SearchAndCreateHistoricalAction/ActionSummary/loader';
import { LoaderCampaigns } from '../pages/main/workOrders/Campaigns/loader';
import { tableIds } from '../components/display/tables/types';
import { EditCampaignLoader } from '../pages/main/workOrders/EditCampaign/loader';
import { LoaderCampaignDetailed } from '../pages/main/workOrders/CampaignDetailed/loader';
import { LoaderSearchAndCreateHistoricalWorkOrderSummary } from '../pages/main/workOrders/SearchAndCreateHistoricalOrder/WorkOrderSummary/loader';
import { AddBasicInfoAccidentLoader } from '../pages/main/workOrders/AddBasicInfoAccident/loader';
import { NewAccidentLoader } from '../pages/main/workOrders/NewAccident/loader';
import { VehicleLoader } from '../pages/main/workOrders/AddVehicle/loader';
import { OperatorsAndVehiclesLoader } from '../pages/main/workOrders/NewAction/OperatorsAndVehicles/loader';
import { NewActionLoader } from '../pages/main/workOrders/NewAction/loader';
import { LocationLoader } from '../pages/main/workOrders/NewAction/Location/loader';
import { BuildingLoader } from '../pages/main/workOrders/NewAction/Edition/loader';
import { MaterialsLoader } from '../pages/main/workOrders/NewAction/Materials/loader';
import { MachineryLoader } from '../pages/main/workOrders/NewAction/Machinery/loader';
import { AnimalsLoader } from '../pages/main/workOrders/NewAction/Animals/loader';
import { ActionSummaryLoader } from '../pages/main/workOrders/NewAction/ActionSummary/loader';
import { BudgetItemsLoader } from '../pages/main/workOrders/NewAction/BudgetItems/loader';
import { budgetItemsLoader as budgetItemsValidationLoader } from '../pages/main/workOrders/ActionsValidation/BudgetItems/loader';
import { LoaderGeneralViewCommunicationSummary } from '../pages/main/workOrders/GeneralView/CommunicationsPage/CommunicationSummary/loader';
import { LoaderCommunicationById } from '../pages/main/workOrders/GeneralView/CommunicationsPage/CommunicationSummary/loaderById';
import { LoaderActionPageOrder } from '../pages/main/workOrders/NewAction/ActionPage/loader';
import { LoaderCampaignSummary } from '../pages/main/workOrders/EditCampaign/CampaignSummary/loader';
import { ActionsValidationLoader } from '../pages/main/workOrders/ActionsValidation/loader';
import { OperatorsAndVehiclesValidationLoader } from '../pages/main/workOrders/ActionsValidation/OperatorsAndVehicles/loader';
import { LoaderGeneralViewAccidentSummary } from '../pages/main/workOrders/GeneralView/AccidentsPage/AccidentPageSummary/loader';
import { LoaderAccidentById } from '../pages/main/workOrders/GeneralView/AccidentsPage/AccidentPageSummary/loaderById';
import { LoaderAccidentGeneralView } from '../pages/main/workOrders/GeneralView/AccidentsPage/loader';
import { LoaderMultimediaGeneralView } from '../pages/main/workOrders/GeneralView/MultimediaPage/loader';
import { LoaderGeneralViewMultimediaGrid } from '../pages/main/workOrders/GeneralView/MultimediaPage/MultimediaGrid/loader';
import { LoaderCommunicationGeneralView } from '../pages/main/workOrders/GeneralView/CommunicationsPage/loader';
import { LoaderActionsGeneralView } from '../pages/main/workOrders/GeneralView/ActionsPage/loader';
// Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — misma factoría
// `makeLoader`, alias propio para las 2 pestañas del LISTADO del módulo RV
// (Actuacions/Multimedia — SIN Accidentes/Comunicaciones, el modelo las
// excluye). `containerTypes` ESTÁTICO (no `getContainerId` — no hay `:id`
// en estas 2 rutas, son hermanas estáticas de `:routeId`, no un binding).
import { makeLoader as makeRouteActionsLoader } from '../pages/main/workOrders/GeneralView/ActionsPage/loader';
import { makeLoader as makeRouteMultimediaLoader } from '../pages/main/workOrders/GeneralView/MultimediaPage/loader';
// Pestañas de módulo del LISTADO de Campañas (`GeneralViewCampaignPage`,
// `/work-orders/campaign`) — misma factoría `makeLoader`, alias propio
// (mismo criterio que `makeRouteActionsLoader`/`makeRouteMultimediaLoader`
// arriba). `containerTypes` ESTÁTICO — sin `getContainerId`, no hay `:id`
// en estas 4 rutas, son hermanas estáticas del `index` de `campaign`.
import { makeLoader as makeCampaignListActionsLoader } from '../pages/main/workOrders/GeneralView/ActionsPage/loader';
import { makeLoader as makeCampaignListAccidentsLoader } from '../pages/main/workOrders/GeneralView/AccidentsPage/loader';
import { makeLoader as makeCampaignListCommunicationsLoader } from '../pages/main/workOrders/GeneralView/CommunicationsPage/loader';
import { makeLoader as makeCampaignListMultimediaLoader } from '../pages/main/workOrders/GeneralView/MultimediaPage/loader';
import { LocationValidationLoader } from '../pages/main/workOrders/ActionsValidation/Location/loader';
import { EditionValidationLoader } from '../pages/main/workOrders/ActionsValidation/Edition/loader';
import { MaterialsValidationLoader } from '../pages/main/workOrders/ActionsValidation/Materials/loader';
import { MachineryValidationLoader } from '../pages/main/workOrders/ActionsValidation/Machinery/loader';
import { AnimalsValidationLoader } from '../pages/main/workOrders/ActionsValidation/Animals/loader';
import { LoaderActionValidationPageOrder } from '../pages/main/workOrders/ActionsValidation/ActionPage/loader';
import { LoaderOrderMultimediaPage } from '../pages/main/workOrders/WorkOrderDetailed/MultimediaPage/loader';
import { VehiclesManagementLoader } from '../pages/main/management/vehicles/loader';
import { UsersManagementLoader } from '../pages/main/management/users/loader';
import { TeamsManagementLoader } from '../pages/main/management/teams/loader';
import { GroupsManagementLoader } from '../pages/main/management/groups/loader';
import { MaterialsManagementLoader } from '../pages/main/management/materials/loader';
import { EditAccidentWorkOrderLoader } from '../pages/main/workOrders/EditAccident/WorkOrder/loader';
import { EditBasicInfoAccidentLoader } from '../pages/main/workOrders/EditAccident/BasicInfo/loader';
import { VehicleEditLoader } from '../pages/main/workOrders/EditAccident/DamagesAndVehicles/loader';
import { EditCommunicationLoader } from '../pages/main/workOrders/EditCommunication/loader';
import { EditCommunicationSummary } from '../pages/main/workOrders/EditCommunication/CommunicationSummary/action';

// Actions
import { ActionLogin } from '../pages/auth/Login/action';
import { ActionResetPassword } from '../pages/auth/ResetPassword/action';
import { ActionForgotPassword } from '../pages/auth/ForgotPassword/action';

import { ActionLogout } from '../components/display/UserMenu/action';
import { ActionNewCommunicationSummary } from '../pages/main/comunications/NewComunication/CommunicationSummary/action';
import { ActionCommunicationDuplicateSummary } from '../pages/main/comunications/DuplicateComunication/CommunicationSummary/action';
import { ActionNewWorkOrderSummary } from '../pages/main/workOrders/NewOrder/WorkOrderSummary/action';
import { ActionNewWorkOrderDetailed } from '../pages/main/workOrders/WorkOrderDetailed/action';
import OrderMultimediaPage from '../pages/main/workOrders/WorkOrderDetailed/MultimediaPage';
import { ActionNewWorkOrderDuplicateSummary } from '../pages/main/workOrders/DuplicateOrder/WorkOrderSummary/action';
import { ActionCampaignSummary } from '../pages/main/workOrders/NewCampaign/CampaignSummary/action';
import { ActionCampaignDetailed } from '../pages/main/workOrders/CampaignDetailed/action';
import { ActionEditCampaignSummary } from '../pages/main/workOrders/EditCampaign/CampaignSummary/action';
import { ActionVehicles } from '../pages/main/workOrders/AddVehicle/Action/action';
import { AnimalsAction } from '../pages/main/workOrders/NewAction/Animals/action';
import { ActionNewAction } from '../pages/main/workOrders/NewAction/action';
import { ActionNewCommunication } from '../pages/main/comunications/NewComunication/action';
import { AnimalsValidationAction } from '../pages/main/workOrders/ActionsValidation/Animals/action';
import { AccidentNewWorkOrderDetailed } from '../pages/main/workOrders/GeneralView/AccidentsPage/AccidentPageSummary/action';
import { ActionEditVehicles } from '../pages/main/workOrders/EditAccident/DamagesAndVehicles/Action/action';

// Context
import { useAuth, AuthContextType } from '../hooks/useAuth';
import { useAppConfig, AppConfigContextType } from '../hooks/useAppConfig';

// Components Utils
import Fallback from '../components/navigation/Fallback';

// Constants and Types
import { formOrderNaming } from '../components/forms/NewWorkOrderForm/constants';
import { FeatureRoute } from './FeatureRoute';
import { layoutTabReset } from '../pages/main/comunications/components/types';
import { AccidentRoute } from '../types/route';

// Redux state
import { store } from '../state/configureStore';
import { AccidentNewAccident } from '../pages/main/workOrders/NewAccident/action';
import { Role } from '../types/auth';

import { AnimalsManagementLoader } from '../pages/main/management/animals/loader';
import AnimalsManagementPage from '../pages/main/management/animals';
import { ExternalCompaniesManagementLoader } from '../pages/main/management/externalCompanies/loader';
import ExternalCompaniesManagementPage from '../pages/main/management/externalCompanies';
import { RoutePresetsManagementLoader } from '../pages/main/management/routePresets/loader';
import RoutePresetsManagementPage from '../pages/main/management/routePresets';
import { MachineryManagementLoader } from '../pages/main/management/machinery/loader';
import MachineryManagementPage from '../pages/main/management/machinery';
import { BudgetCategoriesManagementLoader } from '../pages/main/management/budgetCategories/loader';
import BudgetCategoriesManagementPage from '../pages/main/management/budgetCategories';
import { BudgetItemsManagementLoader } from '../pages/main/management/budgetItems/loader';
import BudgetItemsManagementPage from '../pages/main/management/budgetItems';
import EditVehiclePage from '../pages/main/workOrders/EditAccident/DamagesAndVehicles';

// Admin
import AdminRoutesLayout from '../pages/main/admin/adminRouteLayout';
import TenantsAdminPage from '../pages/main/admin/tenants';
import { TenantsAdminLoader } from '../pages/main/admin/tenants/loader';
import TenantDetailPage from '../pages/main/admin/tenantDetail';
import { TenantDetailLoader } from '../pages/main/admin/tenantDetail/loader';

export let router: DataRouter;
// 🔴 Esta variable se llamaba `sentryCreateBrowserRouter` desde el `first
// commit` (2025-08-26) pero tenía asignado el `createBrowserRouter` CRUDO: el
// nombre mentía y la app nunca tuvo instrumentación de router. No fue una
// regresión de la migración a React Router 7 — `git log -S` sobre
// `wrapCreateBrowserRouter` en todo el historial no devuelve nada.
//
// El wrap da transacciones de navegación con la ruta PARAMETRIZADA
// (`/work-orders/:id` en lugar de `/work-orders/6a9f…`). Sin él, cada id
// generaba su propia transacción y no se podía agregar nada.
//
// En @sentry/react 10.57.0 la función es `wrapCreateBrowserRouterV7`;
// `wrapCreateBrowserRouter` (sin sufijo) es la variante v6 y NO existe aquí.
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// 🔴 El router se crea UNA SOLA VEZ por carga de página.
//
// Antes `createBrowserRouter()` se llamaba en el cuerpo del render de `Router`,
// así que CADA re-render —y `useAuth()` provoca uno en cada `setUser`—
// fabricaba un router nuevo. `createBrowserRouter` llama a `.initialize()` al
// construirse, de modo que cada instancia enganchaba su propio listener de
// historia y ninguna se desechaba jamás (`dispose()` no se llama en ningún
// sitio). Con varios routers vivos a la vez pasaban tres cosas, las tres
// medidas en el navegador:
//
//   1. `RouterProvider` guarda el estado con `useState(router.state)`, y ese
//      valor inicial solo se usa en el PRIMER montaje: al cambiar de instancia
//      seguía PINTANDO el estado del router viejo mientras el contexto de
//      navegación ya era el nuevo.
//   2. `useNavigate()` en un data router llama a `router.navigate()`, que
//      resuelve las rutas relativas contra `router.state.location` — la del
//      router nuevo, que se quedó en `/login`. De ahí el `/login/new-order`
//      con «Not Found» al pulsar «Nueva O.T».
//   3. La barra de direcciones se la disputaban dos routers: el nuevo
//      redirigía a `/work-orders` y, 71 ms después, el viejo terminaba la
//      acción POST del login y su `completeNavigation` reescribía `/login`.
//
// El singleton va a nivel de MÓDULO, no en un `useMemo([])`: con `StrictMode`
// se midieron **4 creaciones** por arranque con el `useMemo`, porque React
// reevalúa la factoría en el doble render del montaje y vuelve a montar el
// componente. Así es además el patrón que documenta React Router
// (`const router = createBrowserRouter([...])` en el nivel superior).
let routerSingleton: DataRouter | undefined;

// El contexto vigente, que el componente refresca en cada render.
let liveAuth: AuthContextType | null = null;
let liveAppConfig: AppConfigContextType | null = null;

// Los loaders y actions siguen recibiendo el contexto por parámetro, pero es
// esta fachada estable: se construye una vez con el router y sus getters leen
// SIEMPRE el valor vigente, porque el cuerpo del loader accede a las
// propiedades al ejecutarse, no al construirse la ruta. Sin la fachada, el
// router quedaría casado para siempre con la sesión que hubiera al arrancar
// (es decir, sin usuario).
const userData: AuthContextType = {
  get user() {
    return liveAuth!.user;
  },
  get setUser() {
    return liveAuth!.setUser;
  },
  get activeTenantId() {
    return liveAuth!.activeTenantId;
  },
  get availableTenants() {
    return liveAuth!.availableTenants;
  },
  get setActiveTenantId() {
    return liveAuth!.setActiveTenantId;
  },
  get currentRole() {
    return liveAuth!.currentRole;
  },
};

const appConfigData: AppConfigContextType = {
  get appConfig() {
    return liveAppConfig!.appConfig;
  },
  get availableConcessionIds() {
    return liveAppConfig!.availableConcessionIds;
  },
  get loading() {
    return liveAppConfig!.loading;
  },
  get error() {
    return liveAppConfig!.error;
  },
  refetch: () => liveAppConfig!.refetch(),
  clearAppConfig: () => liveAppConfig!.clearAppConfig(),
};

export default function Router() {
  liveAuth = useAuth();
  liveAppConfig = useAppConfig();

  if (!routerSingleton) {
    const routes = createRoutesFromElements(
      // La raíz necesita su propio `errorElement`. Los dos bloques de abajo
      // (AuthLayout y MainLayout) ya tenían el suyo, pero una URL que no casa
      // con NINGUNA ruta falla aquí arriba, donde no había nada que la
      // recogiera: React Router caía en su ErrorBoundary por defecto y pintaba
      // la pantalla de desarrollo «💿 Hey developer 👋 You can provide a way
      // better UX than this…», en inglés y con jerga de programador.
      // Pasaba con cualquier enlace viejo o mal escrito; se encontró con
      // `/time-management`, que se movió a `/reporting/time-management`.
      <Route errorElement={<ErrorPage />}>
        <Route index path={'/'} loader={LoaderIndexPage} />
        <Route element={<AuthLayout />} errorElement={<ErrorPage />}>
          <Route
            path="logout"
            action={ActionLogout(store)(userData, appConfigData)}
          />
          <Route
            index
            path="login"
            element={<LoginPage />}
            action={ActionLogin(userData)}
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
            action={ActionResetPassword(userData)}
          />
          <Route
            path="/forgot-password"
            element={<ForgotPasswordPage />}
            action={ActionForgotPassword}
          />
        </Route>

        <Route
          element={<MainLayout />}
          loader={LoaderBaseDataMain(userData)}
          errorElement={<ErrorPage />}
        >
          {/* <Route index path={'dashboard'} element={<DashboardPage />} /> */}
          <Route
            path={'communication'}
            element={
              <RolesAuthRoute
                roles={[
                  Role.SuperAdmin,
                  Role.Coex,
                  Role.Capataz,
                  Role.RoomTeam,
                ]}
              >
                <CommunicationsLayout />
              </RolesAuthRoute>
            }
          >
            <Route
              index
              element={
                <Fallback dependsOn="data">
                  <NewCommunicationPage />
                </Fallback>
              }
              loader={NewCommunicationLoader(userData)}
            />
            <Route
              action={ActionNewCommunicationSummary(store)}
              element={<NewCommunicationSummary />}
              path={'summary'}
            />
            <Route
              path={'duplicate-communication'}
              element={
                <LayoutResetIndex
                  resetType={layoutTabReset.ONLY_COMMUNICATION}
                />
              }
            >
              <Route
                index
                loader={LoaderCommunicationDuplicates(userData)}
                element={
                  <Fallback dependsOn="data">
                    <DuplicateCommunicationPage />
                  </Fallback>
                }
              />
              <Route
                action={ActionCommunicationDuplicateSummary(store)}
                element={<NewCommunicationDuplicateSummaryPage />}
                path={'summary'}
              />
              <Route
                action={ActionCommunicationDuplicateSummary(store)}
                loader={LoaderCommunicationDuplicateSummary}
                element={
                  <Fallback dependsOn="data">
                    <NewCommunicationDuplicateSummaryPage />
                  </Fallback>
                }
                path={'summary/:id'}
              />
              <Route
                path={'new-communication-duplicate'}
                element={
                  <Fallback dependsOn="data">
                    <NewCommunicationDuplicatePage />
                  </Fallback>
                }
                loader={NewCommunicationDuplicateLoader(userData)}
              />
              <Route
                path={'new-communication-duplicate/:id'}
                element={
                  <EntityScreen>
                    <Fallback dependsOn="data">
                      <NewCommunicationDuplicatePage />
                    </Fallback>
                  </EntityScreen>
                }
                loader={NewCommunicationDuplicateLoader(userData)}
              />
            </Route>
            {/* Movido a /reporting/historical-communications */}
            <Route
              path={'search-create-historical-communication/*'}
              element={
                <Navigate to="/reporting/historical-communications" replace />
              }
            />
          </Route>
          <Route path={'work-orders'} element={<WorkOrdersLayout />}>
            <Route
              element={
                <Fallback dependsOn="data">
                  <GeneralViewPage />
                </Fallback>
              }
              loader={LoaderWorkOrdersGeneralView(userData)}
            >
              <Route index element={<WorkOrdersPage />} />
              <Route path="accidents">
                <Route
                  index
                  element={
                    <Fallback dependsOn="data">
                      <AccidentsPage />
                    </Fallback>
                  }
                  loader={LoaderAccidentGeneralView(userData)}
                />
              </Route>
              <Route
                path="communications"
                loader={LoaderCommunicationGeneralView(userData)}
                element={
                  <Fallback dependsOn="data">
                    <CommunicationsPage />
                  </Fallback>
                }
              />

              <Route
                path="multimedia"
                element={
                  <Fallback dependsOn="data">
                    <MultimediaPage />
                  </Fallback>
                }
                loader={LoaderMultimediaGeneralView(userData)}
              />

              <Route
                path="actions"
                element={
                  <Fallback dependsOn="data">
                    <ActionsPage />
                  </Fallback>
                }
                loader={LoaderActionsGeneralView(userData)}
              />
            </Route>
            <Route path="accidents">
              <Route path="edit-accident">
                <Route
                  path=":id"
                  loader={EditAccidentWorkOrderLoader(userData)}
                  element={
                    <EntityScreen>
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <EditAccidentWorkOrderPage />
                        </Fallback>
                      </RolesAuthRoute>
                    </EntityScreen>
                  }
                />
                {/* Pasos 2 y 3 de editar accidente: cambiar de accidente sin salir
                    de la ruta (atrás/adelante) monta la pantalla de cero (R5, spec
                    2026-11-02 wizard-draft-ownership). */}
                <Route
                  path=":id"
                  element={
                    <EntityScreen>
                      <Outlet />
                    </EntityScreen>
                  }
                >
                  <Route
                    path="basic-info"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <EditBasicInfoAccidentPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={EditBasicInfoAccidentLoader(userData)}
                  />
                  <Route
                    path={'damages-and-vehicles'}
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <EditVehiclePage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    action={ActionEditVehicles}
                    loader={VehicleEditLoader(userData)}
                  />
                </Route>
              </Route>
            </Route>
            <Route
              path="accidents/:id"
              loader={LoaderGeneralViewAccidentSummary(userData)}
              action={AccidentNewWorkOrderDetailed}
              element={
                <Fallback dependsOn="data">
                  <AccidentSummaryGeneralView />
                </Fallback>
              }
            />
            {/* Ruta para navegación directa a accidente por ID (desde ShowOT) */}
            <Route
              path="accidents/detail/:accidentId"
              loader={LoaderAccidentById(userData)}
              action={AccidentNewWorkOrderDetailed}
              element={
                <Fallback dependsOn="data">
                  <AccidentSummaryGeneralView />
                </Fallback>
              }
            />
            <Route
              loader={LoaderGeneralViewCommunicationSummary}
              path="communications/:id"
              element={
                <Fallback dependsOn="data">
                  <CommunicationSummaryGeneralView />
                </Fallback>
              }
            />
            {/* Ruta para navegación directa a comunicación por ID (desde ShowOT) */}
            <Route
              path="communications/detail/:communicationId"
              loader={LoaderCommunicationById}
              element={
                <Fallback dependsOn="data">
                  <CommunicationSummaryGeneralView />
                </Fallback>
              }
            />
            {/* Ruta para editar comunicación desde work-orders (siguiendo patrón de edit-accident) */}
            {/* Cambiar de comunicación sin salir de la ruta (atrás/adelante) monta la
                pantalla de cero, también el resumen (S2, auditoría 2026-11-02). */}
            <Route
              path="communications/edit-communication/:id"
              element={
                <EntityScreen>
                  <Outlet />
                </EntityScreen>
              }
            >
              <Route
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <EditCommunicationPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                index
                loader={EditCommunicationLoader(userData)}
              />
              <Route
                action={EditCommunicationSummary(store)}
                element={<EditCommunicationSummaryPage />}
                path={'summary'}
              />
            </Route>
            <Route
              loader={LoaderGeneralViewMultimediaGrid(userData)}
              path="multimedia/:id"
              element={
                <Fallback dependsOn="data">
                  <MultimediaGridGeneralView />
                </Fallback>
              }
            />
            <Route path=":id">
              <Route
                index
                action={ActionNewWorkOrderDetailed}
                loader={LoaderWorkOrderDetailed}
                element={
                  <Fallback dependsOn="data">
                    <WorkOrderDetailed />
                  </Fallback>
                }
              />
              <Route
                path={'new-action'}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.Tablet,
                      Role.OperatingTeam,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <NewActionPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={NewActionLoader(userData)}
                action={ActionNewAction}
              />
              <Route
                path={'multimedia'}
                element={
                  <Fallback dependsOn="data">
                    <OrderMultimediaPage />
                  </Fallback>
                }
                loader={LoaderOrderMultimediaPage(userData)}
              />
              <Route path={'new-action'}>
                <Route
                  path={'details'}
                  element={
                    <EntityScreen entityKey={readCreationDraftId}>
                      <Fallback dependsOn="data">
                        <ActionPage />
                      </Fallback>
                    </EntityScreen>
                  }
                  loader={LoaderActionPageOrder(userData)}
                >
                  {/* `index` y `path` son EXCLUYENTES: estaban en el mismo <Route> y
                      ganaba el `index`, así que el `path="*"` se ignoraba y el rescate
                      NUNCA funcionó — una pestaña inexistente daba «No routes matched
                      location» y pantalla de error en vez de llevar a la primera.

                      🔴 El destino del catch-all lleva `../` y el del índice no. No es
                      un descuido: es lo que manda la guía de React Router al partir una
                      ruta splat («include the extra `..` relative segment»). Desde el
                      `*` hay un nivel de ruta MÁS que desde el índice, y sin ese `..`
                      el destino se resuelve DENTRO del propio splat: vuelve a no casar,
                      el `*` recaptura y la URL crece sin fin. Verificado: bucle infinito.

                      `..` sube un nivel de RUTA, no un segmento de URL, así que aguanta
                      aunque el splat capture varios segmentos. */}
                  <Route index element={<Navigate to="operators-and-vehicles" replace />} />
                  <Route path="*" element={<Navigate to="../operators-and-vehicles" replace />} />
                  <Route
                    path="operators-and-vehicles"
                    element={
                      <Fallback dependsOn="data">
                        <OperatorsAndVehiclesFormPage />
                      </Fallback>
                    }
                    loader={OperatorsAndVehiclesLoader(userData)}
                  />
                  <Route
                    path="location"
                    element={
                      <Fallback dependsOn="data">
                        <LocationFormPage />
                      </Fallback>
                    }
                    loader={LocationLoader}
                  />
                  <Route
                    path="edition"
                    element={
                      <Fallback dependsOn="data">
                        <EditionActionFormPage />
                      </Fallback>
                    }
                    loader={BuildingLoader(userData)}
                  />
                  <Route
                    path="budget-items"
                    element={
                      <RolesAuthRoute roles={[Role.SuperAdmin, Role.Coex]}>
                        <Fallback dependsOn="data">
                          <BudgetItemsFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={BudgetItemsLoader}
                  />
                  <Route
                    path="materials"
                    element={
                      <Fallback dependsOn="data">
                        <MaterialsFormPage />
                      </Fallback>
                    }
                    loader={MaterialsLoader}
                  />
                  <Route
                    path="machinery"
                    element={
                      <Fallback dependsOn="data">
                        <MachineryFormPage />
                      </Fallback>
                    }
                    loader={MachineryLoader}
                  />
                  <Route
                    path="animals"
                    element={
                      <Fallback dependsOn="data">
                        <AnimalsFormPage />
                      </Fallback>
                    }
                    loader={AnimalsLoader()}
                    action={AnimalsAction}
                  />
                </Route>
                <Route
                  path="summary/:id"
                  element={
                    <EntityScreen>
                      <Fallback dependsOn="data">
                        <ActionSummaryPage />
                      </Fallback>
                    </EntityScreen>
                  }
                  loader={ActionSummaryLoader}
                />
              </Route>
              <Route
                path={'new-accident'}
                element={
                  <EntityScreen>
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.OperatingTeam,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <NewAccidentPage />
                      </Fallback>
                    </RolesAuthRoute>
                  </EntityScreen>
                }
                loader={NewAccidentLoader(userData)}
                action={AccidentNewAccident}
              />
              {/* Pasos 2 y 3 del alta de accidente: cambiar de padre sin salir de
                  la ruta (atrás/adelante) monta la pantalla de cero (S4, spec
                  2026-11-02 wizard-draft-ownership). */}
              <Route
                path={'new-accident'}
                element={
                  <EntityScreen>
                    <Outlet />
                  </EntityScreen>
                }
              >
                <Route
                  path="basic-info"
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.OperatingTeam,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <AddBasicInfoAccidentPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={AddBasicInfoAccidentLoader(userData)}
                />

                <Route
                  path={'damages-and-vehicles'}
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.OperatingTeam,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <AddVehiclePage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  action={ActionVehicles(AccidentRoute.BASE)}
                  loader={VehicleLoader(userData)}
                />
              </Route>
              {/* Alta de comunicación de la OT: cambiar de padre sin salir de la
                  ruta (atrás/adelante) monta la pantalla de cero (K3, QA
                  2026-11-03; spec 2026-11-02 wizard-draft-ownership). */}
              <Route
                path={'new-communication'}
                action={ActionNewCommunication}
                element={
                  <EntityScreen>
                    <Outlet />
                  </EntityScreen>
                }
              >
                <Route
                  index
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <NewCommunicationPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={NewCommunicationLoader(userData)}
                />
                <Route
                  path="summary"
                  action={ActionNewCommunicationSummary(store)}
                  element={<NewCommunicationSummary />}
                />
              </Route>
            </Route>
            <Route path={'new-order'}>
              <Route
                index
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <NewOrderPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={NewWorkOrderLoader(
                  userData,
                  formOrderNaming.NEW_WORK_ORDER_FORM
                )}
              />
            </Route>
            <Route
              action={ActionNewWorkOrderSummary}
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                    Role.Tablet,
                  ]}
                >
                  <NewWorkOrderSummary />
                </RolesAuthRoute>
              }
              path={'summary'}
            />
            <Route path={'duplicate-order'}>
              <Route
                index
                element={
                  <Fallback dependsOn="data">
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <DuplicateOrderPage />
                    </RolesAuthRoute>
                  </Fallback>
                }
                loader={LoaderOrderDuplicates(userData)}
              />
              <Route
                path={'new-order-duplicate'}
                element={
                  <Fallback dependsOn="data">
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <NewOrderDuplicatePage />
                    </RolesAuthRoute>
                  </Fallback>
                }
                loader={NewWorkOrderDuplicateLoader(userData)}
              />
              <Route
                path={'new-order-duplicate/:id'}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <EntityScreen>
                      <Fallback dependsOn="data">
                        <NewOrderDuplicatePage />
                      </Fallback>
                    </EntityScreen>
                  </RolesAuthRoute>
                }
                loader={NewWorkOrderDuplicateLoader(userData)}
              />
              <Route
                path={'summary'}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <WorkOrderDuplicateSummaryPage />
                  </RolesAuthRoute>
                }
                action={ActionNewWorkOrderDuplicateSummary}
              />
              <Route
                path={'summary/:id'}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <WorkOrderDuplicateSummaryPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                action={ActionNewWorkOrderDuplicateSummary}
                loader={LoaderOrderSummaryDuplicates}
              />
            </Route>
            {/* Movido a /reporting/historical-generic-orders */}
            <Route
              path={'search-create-historical-order/*'}
              element={
                <Navigate to="/reporting/historical-generic-orders" replace />
              }
            />
            {/* Movido a /reporting/historical-campaigns */}
            <Route
              path={'search-create-historical-campaign/*'}
              element={
                <Navigate to="/reporting/historical-campaigns" replace />
              }
            />
            <Route path={'search-create-historical-action'}>
              <Route
                index
                element={
                  <RolesAuthRoute
                    roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                  >
                    <Fallback dependsOn="data">
                      <SearchAndCreateHistoricalActionPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={SearchAndCreateHistoricalActionLoader(userData)}
              />
              <Route
                path={'summary'}
                element={
                  <RolesAuthRoute
                    roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                  >
                    <SearchAndCreateHistoricalActionSummary />
                  </RolesAuthRoute>
                }
              />
              <Route
                path={'summary/:id'}
                element={
                  <RolesAuthRoute
                    roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                  >
                    <Fallback dependsOn="data">
                      <SearchAndCreateHistoricalActionSummary />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={HistoricalActionSummaryLoader}
              />
            </Route>
            <Route path={'campaign'}>
              {/* Pestañas de módulo del LISTADO de Campañas (espejo EXACTO
                  del patrón RV `GeneralViewRoutePage`, ver
                  `Campaigns/GeneralViewCampaign/index.tsx`): wrapper
                  Tabs+Outlet con [Campañas|Actuacions|Accidents|
                  Comunicacions|Multimedia]. Primera pestaña = el `index`
                  actual, byte-idéntico (mismo `CampaignsPage`/
                  `LoaderCampaigns`). Las otras 4 son rutas ESTÁTICAS
                  hermanas de `:id` (React Router prioriza el segment
                  estático — mismo precedente que `new-route`/`actions`
                  del módulo RV) — filtran por `containerTypes: ['campaign']`
                  (TIPO de origen: TODAS las campañas). El DETALLE de una
                  campaña no tiene pestañas de entidad: su `index` (ShowOT)
                  ya lista las hijas, igual que el de una O.T. genérica. */}
              <Route
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.Tablet,
                      Role.RoomTeam,
                    ]}
                  >
                    <GeneralViewCampaignPage />
                  </RolesAuthRoute>
                }
              >
                <Route
                  index
                  element={
                    <Fallback dependsOn="data">
                      <CampaignsPage />
                    </Fallback>
                  }
                  loader={LoaderCampaigns(userData)}
                />
                <Route
                  path="actions"
                  element={
                    <Fallback dependsOn="data">
                      <CampaignListActionsTab />
                    </Fallback>
                  }
                  loader={makeCampaignListActionsLoader({
                    containerTypes: ['campaign'],
                    tableId: tableIds.CAMPAIGN_LIST_ACTIONS,
                  })(userData)}
                />
                <Route
                  path="accidents"
                  element={
                    <Fallback dependsOn="data">
                      <CampaignListAccidentsTab />
                    </Fallback>
                  }
                  loader={makeCampaignListAccidentsLoader({
                    containerTypes: ['campaign'],
                    tableId: tableIds.CAMPAIGN_LIST_ACCIDENTS,
                  })(userData)}
                />
                <Route
                  path="communications"
                  element={
                    <Fallback dependsOn="data">
                      <CampaignListCommunicationsTab />
                    </Fallback>
                  }
                  loader={makeCampaignListCommunicationsLoader({
                    containerTypes: ['campaign'],
                    tableId: tableIds.CAMPAIGN_LIST_COMMUNICATIONS,
                  })(userData)}
                />
                <Route
                  path="multimedia-list"
                  element={
                    <Fallback dependsOn="data">
                      <CampaignListMultimediaTab />
                    </Fallback>
                  }
                  loader={makeCampaignListMultimediaLoader({
                    containerTypes: ['campaign'],
                    tableId: tableIds.CAMPAIGN_LIST_MULTIMEDIA,
                  })(userData)}
                />
              </Route>
              <Route path=":id">
                <Route
                  index
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.RoomTeam,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <CampaignDetailed />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  action={ActionCampaignDetailed}
                  loader={LoaderCampaignDetailed}
                />
                <Route
                  path={'multimedia'}
                  element={
                    <Fallback dependsOn="data">
                      <OrderMultimediaPage />
                    </Fallback>
                  }
                  loader={LoaderOrderMultimediaPage(userData)}
                />
                <Route
                  path={'new-action'}
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.OperatingTeam,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <NewActionPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={NewActionLoader(userData)}
                  action={ActionNewAction}
                />
                <Route path="new-action">
                  <Route
                    path={'details'}
                    element={
                      <EntityScreen entityKey={readCreationDraftId}>
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <ActionPage />
                          </Fallback>
                        </RolesAuthRoute>
                      </EntityScreen>
                    }
                    loader={LoaderActionPageOrder(userData)}
                  >
                    {/* `index` y `path` son EXCLUYENTES: estaban en el mismo <Route> y
                        ganaba el `index`, así que el `path="*"` se ignoraba y el rescate
                        NUNCA funcionó — una pestaña inexistente daba «No routes matched
                        location» y pantalla de error en vez de llevar a la primera.

                        🔴 El destino del catch-all lleva `../` y el del índice no. No es
                        un descuido: es lo que manda la guía de React Router al partir una
                        ruta splat («include the extra `..` relative segment»). Desde el
                        `*` hay un nivel de ruta MÁS que desde el índice, y sin ese `..`
                        el destino se resuelve DENTRO del propio splat: vuelve a no casar,
                        el `*` recaptura y la URL crece sin fin. Verificado: bucle infinito.

                        `..` sube un nivel de RUTA, no un segmento de URL, así que aguanta
                        aunque el splat capture varios segmentos. */}
                    <Route index element={<Navigate to="operators-and-vehicles" replace />} />
                    <Route path="*" element={<Navigate to="../operators-and-vehicles" replace />} />
                    <Route
                      path="operators-and-vehicles"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <OperatorsAndVehiclesFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={OperatorsAndVehiclesLoader(userData)}
                    />
                    <Route
                      path="location"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <LocationFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={LocationLoader}
                    />
                    <Route
                      path="edition"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <EditionActionFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={BuildingLoader(userData)}
                    />
                    <Route
                      path="budget-items"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <BudgetItemsFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={BudgetItemsLoader}
                    />
                    <Route
                      path="materials"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <MaterialsFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={MaterialsLoader}
                    />
                    <Route
                      path="machinery"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <MachineryFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={MachineryLoader}
                    />
                    <Route
                      path="animals"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <AnimalsFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={AnimalsLoader()}
                      action={AnimalsAction}
                    />
                  </Route>
                  <Route
                    path="summary/:id"
                    element={
                      <EntityScreen>
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <ActionSummaryPage />
                          </Fallback>
                        </RolesAuthRoute>
                      </EntityScreen>
                    }
                    loader={ActionSummaryLoader}
                  />
                </Route>
                <Route
                  path={'new-accident'}
                  element={
                    <EntityScreen>
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                          Role.OperatingTeam,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <NewAccidentPage />
                        </Fallback>
                      </RolesAuthRoute>
                    </EntityScreen>
                  }
                  loader={NewAccidentLoader(userData)}
                  action={AccidentNewAccident}
                />
                {/* Pasos 2 y 3 del alta de accidente: cambiar de padre sin salir de
                    la ruta (atrás/adelante) monta la pantalla de cero (S4, spec
                    2026-11-02 wizard-draft-ownership). */}
                <Route
                  path={'new-accident'}
                  element={
                    <EntityScreen>
                      <Outlet />
                    </EntityScreen>
                  }
                >
                  <Route
                    path="basic-info"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                          Role.OperatingTeam,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <AddBasicInfoAccidentPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={AddBasicInfoAccidentLoader(userData)}
                  />

                  <Route
                    path={'damages-and-vehicles'}
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                          Role.OperatingTeam,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <AddVehiclePage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    action={ActionVehicles(AccidentRoute.CAMPAIGN)}
                    loader={VehicleLoader(userData)}
                  />
                </Route>
                {/* Alta de comunicación de la campaña: misma regla que en la OT. */}
                <Route
                  path={'new-communication'}
                  action={ActionNewCommunication}
                  element={
                    <EntityScreen>
                      <Outlet />
                    </EntityScreen>
                  }
                >
                  <Route
                    index
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <NewCommunicationPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={NewCommunicationLoader(userData)}
                  />
                  <Route
                    path="summary"
                    action={ActionNewCommunicationSummary(store)}
                    element={<NewCommunicationSummary />}
                  />
                </Route>

                {/* Rutas para ver/editar accidents dentro de campaign */}
                <Route path="accidents">
                  <Route
                    path="detail/:accidentId"
                    loader={LoaderAccidentById(userData)}
                    action={AccidentNewWorkOrderDetailed}
                    element={
                      <Fallback dependsOn="data">
                        <AccidentSummaryGeneralView />
                      </Fallback>
                    }
                  />
                  <Route path="edit-accident">
                    <Route
                      path=":id"
                      loader={EditAccidentWorkOrderLoader(userData)}
                      element={
                        <EntityScreen>
                          <RolesAuthRoute
                            roles={[
                              Role.SuperAdmin,
                              Role.Coex,
                              Role.Capataz,
                              Role.RoomTeam,
                              Role.Tablet,
                            ]}
                          >
                            <Fallback dependsOn="data">
                              <EditAccidentWorkOrderPage />
                            </Fallback>
                          </RolesAuthRoute>
                        </EntityScreen>
                      }
                    />
                    {/* Pasos 2 y 3 de editar accidente: cambiar de accidente sin salir
                        de la ruta (atrás/adelante) monta la pantalla de cero (R5, spec
                        2026-11-02 wizard-draft-ownership). */}
                    <Route
                      path=":id"
                      element={
                        <EntityScreen>
                          <Outlet />
                        </EntityScreen>
                      }
                    >
                      <Route
                        path="basic-info"
                        element={
                          <RolesAuthRoute
                            roles={[
                              Role.SuperAdmin,
                              Role.Coex,
                              Role.Capataz,
                              Role.RoomTeam,
                              Role.Tablet,
                            ]}
                          >
                            <Fallback dependsOn="data">
                              <EditBasicInfoAccidentPage />
                            </Fallback>
                          </RolesAuthRoute>
                        }
                        loader={EditBasicInfoAccidentLoader(userData)}
                      />
                      <Route
                        path="damages-and-vehicles"
                        element={
                          <RolesAuthRoute
                            roles={[
                              Role.SuperAdmin,
                              Role.Coex,
                              Role.Capataz,
                              Role.RoomTeam,
                              Role.Tablet,
                            ]}
                          >
                            <Fallback dependsOn="data">
                              <EditVehiclePage />
                            </Fallback>
                          </RolesAuthRoute>
                        }
                        action={ActionEditVehicles}
                        loader={VehicleEditLoader(userData)}
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Rutas para ver/editar communications dentro de campaign */}
                <Route path="communications">
                  <Route
                    path="detail/:communicationId"
                    loader={LoaderCommunicationById}
                    element={
                      <Fallback dependsOn="data">
                        <CommunicationSummaryGeneralView />
                      </Fallback>
                    }
                  />
                  <Route
                    path="edit-communication/:id"
                    element={
                      <EntityScreen>
                        <Outlet />
                      </EntityScreen>
                    }
                  >
                    <Route
                      index
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <EditCommunicationPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={EditCommunicationLoader(userData)}
                    />
                    <Route
                      path="summary"
                      action={EditCommunicationSummary(store)}
                      element={
                        <EditCommunicationSummaryPage />
                      }
                    />
                  </Route>
                </Route>

                {/* Rutas para ver/editar actions dentro de campaign */}
                <Route path="actions-validation">
                  <Route
                    path="summary/:id"
                    element={
                      <EntityScreen>
                        <Fallback dependsOn="data">
                          <ActionSummaryPage />
                        </Fallback>
                      </EntityScreen>
                    }
                    loader={ActionSummaryLoader}
                  />
                  <Route
                    path=":id"
                    element={
                      <EntityScreen>
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                            Role.Tablet,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <ActionValidationPage />
                          </Fallback>
                        </RolesAuthRoute>
                      </EntityScreen>
                    }
                    loader={LoaderActionValidationPageOrder}
                  >
                    {/* `index` y `path` son EXCLUYENTES: estaban en el mismo <Route> y
                        ganaba el `index`, así que el `path="*"` se ignoraba y el rescate
                        NUNCA funcionó — una pestaña inexistente daba «No routes matched
                        location» y pantalla de error en vez de llevar a la primera.

                        🔴 El destino del catch-all lleva `../` y el del índice no. No es
                        un descuido: es lo que manda la guía de React Router al partir una
                        ruta splat («include the extra `..` relative segment»). Desde el
                        `*` hay un nivel de ruta MÁS que desde el índice, y sin ese `..`
                        el destino se resuelve DENTRO del propio splat: vuelve a no casar,
                        el `*` recaptura y la URL crece sin fin. Verificado: bucle infinito.

                        `..` sube un nivel de RUTA, no un segmento de URL, así que aguanta
                        aunque el splat capture varios segmentos. */}
                    <Route index element={<Navigate to="operators-and-vehicles" replace />} />
                    <Route path="*" element={<Navigate to="../operators-and-vehicles" replace />} />
                    <Route
                      path="operators-and-vehicles"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                            Role.Tablet,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <OperatorsAndVehiclesFormValidationPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={OperatorsAndVehiclesValidationLoader(userData)}
                    />
                    <Route
                      path="location"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                            Role.Tablet,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <LocationActionFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={LocationValidationLoader(userData)}
                    />
                    <Route
                      path="edition"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                            Role.Tablet,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <EditionValidationFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={EditionValidationLoader(userData)}
                    />
                    <Route
                      path="budget-items"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Tablet,
                            Role.OperatingTeam,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <BudgetItemsValidationFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={budgetItemsValidationLoader}
                    />
                    <Route
                      path="materials"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                            Role.Tablet,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <MaterialsValidationFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={MaterialsValidationLoader(userData)}
                      // Paso terminal de la Actuació bàsica (2026-08-11): solo
                      // ella hace submit aquí; la actuación normal navega a
                      // Maquinaria y sigue guardando desde Animales.
                      action={AnimalsValidationAction}
                    />
                    <Route
                      path="machinery"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                            Role.Tablet,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <MachineryValidationFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={MachineryValidationLoader(userData)}
                    />
                    <Route
                      path="animals"
                      element={
                        <RolesAuthRoute
                          roles={[
                            Role.SuperAdmin,
                            Role.Coex,
                            Role.Capataz,
                            Role.RoomTeam,
                            Role.Tablet,
                          ]}
                        >
                          <Fallback dependsOn="data">
                            <AnimalsValidationFormPage />
                          </Fallback>
                        </RolesAuthRoute>
                      }
                      loader={AnimalsValidationLoader(userData)}
                      action={AnimalsValidationAction}
                    />
                  </Route>
                </Route>
              </Route>
              <Route
                path={'new-campaign'}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.Tablet,
                      Role.RoomTeam,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <NewCampaignPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={NewWorkOrderLoader(
                  userData,
                  formOrderNaming.NEW_CAMPAIGN_FORM
                )}
              />
              <Route path={'edit-campaign'}>
                <Route
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.RoomTeam,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <EditCampaignPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={EditCampaignLoader(userData)}
                />
                <Route
                  path={':id'}
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.RoomTeam,
                      ]}
                    >
                      <EntityScreen>
                        <Fallback dependsOn="data">
                          <EditCampaignPage />
                        </Fallback>
                      </EntityScreen>
                    </RolesAuthRoute>
                  }
                  loader={EditCampaignLoader(userData)}
                />
                <Route
                  path={':id/summary'}
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.Tablet,
                        Role.RoomTeam,
                      ]}
                    >
                      <EntityScreen>
                        <Fallback dependsOn="data">
                          <EditCampaignSummaryPage />
                        </Fallback>
                      </EntityScreen>
                    </RolesAuthRoute>
                  }
                  action={ActionEditCampaignSummary}
                  loader={LoaderCampaignSummary}
                />
              </Route>
              <Route
                path="summary"
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.Tablet,
                      Role.RoomTeam,
                    ]}
                  >
                    <CampaignSummary />
                  </RolesAuthRoute>
                }
                action={ActionCampaignSummary}
              />
            </Route>
            <Route path={'actions-validation'}>
              <Route
                index
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <ActionsValidation />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={ActionsValidationLoader(userData)}
              />
              <Route
                path={':id'}
                element={
                  <EntityScreen>
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <ActionValidationPage />
                      </Fallback>
                    </RolesAuthRoute>
                  </EntityScreen>
                }
                loader={LoaderActionValidationPageOrder}
              >
                {/* `index` y `path` son EXCLUYENTES: estaban en el mismo <Route> y
                    ganaba el `index`, así que el `path="*"` se ignoraba y el rescate
                    NUNCA funcionó — una pestaña inexistente daba «No routes matched
                    location» y pantalla de error en vez de llevar a la primera.

                    🔴 El destino del catch-all lleva `../` y el del índice no. No es
                    un descuido: es lo que manda la guía de React Router al partir una
                    ruta splat («include the extra `..` relative segment»). Desde el
                    `*` hay un nivel de ruta MÁS que desde el índice, y sin ese `..`
                    el destino se resuelve DENTRO del propio splat: vuelve a no casar,
                    el `*` recaptura y la URL crece sin fin. Verificado: bucle infinito.

                    `..` sube un nivel de RUTA, no un segmento de URL, así que aguanta
                    aunque el splat capture varios segmentos. */}
                <Route index element={<Navigate to="operators-and-vehicles" replace />} />
                <Route path="*" element={<Navigate to="../operators-and-vehicles" replace />} />
                <Route
                  path="operators-and-vehicles"
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <OperatorsAndVehiclesFormValidationPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={OperatorsAndVehiclesValidationLoader(userData)}
                />
                <Route
                  path="location"
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <LocationActionFormPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={LocationValidationLoader(userData)}
                />
                <Route
                  path="edition"
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <EditionValidationFormPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={EditionValidationLoader(userData)}
                />
                <Route
                  path="budget-items"
                  element={
                    <RolesAuthRoute
                      roles={[Role.SuperAdmin, Role.Coex, Role.Tablet]}
                    >
                      <Fallback dependsOn="data">
                        <BudgetItemsValidationFormPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={budgetItemsValidationLoader}
                />
                <Route
                  path="materials"
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <MaterialsValidationFormPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={MaterialsValidationLoader(userData)}
                  // Paso terminal de la Actuació bàsica (2026-08-11): solo ella
                  // hace submit aquí; la actuación normal navega a Maquinaria y
                  // sigue guardando desde Animales.
                  action={AnimalsValidationAction}
                />
                <Route
                  path="machinery"
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <MachineryValidationFormPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={MachineryValidationLoader(userData)}
                />
                <Route
                  path="animals"
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <AnimalsValidationFormPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                  loader={AnimalsValidationLoader(userData)}
                  action={AnimalsValidationAction}
                />
              </Route>
              <Route
                path="summary/:id"
                element={
                  <EntityScreen>
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <ActionSummaryPage />
                      </Fallback>
                    </RolesAuthRoute>
                  </EntityScreen>
                }
                loader={ActionSummaryLoader}
              />
            </Route>
          </Route>
          {/* Sección Budgets vaciada: su único contenido (histórico) vive ahora en
              /reporting/historical-budgets. Redirect para enlaces antiguos. */}
          <Route
            path={'budgets/*'}
            element={<Navigate to="/reporting/historical-budgets" replace />}
          />
          <Route
            path="management"
            element={
              <RolesAuthRoute
                roles={[
                  Role.SuperAdmin,
                  Role.Coex,
                  Role.Capataz,
                  Role.RoomTeam,
                  Role.Tablet,
                ]}
              >
                <ManagementRoutesLayout />
              </RolesAuthRoute>
            }
          >
            <Route
              loader={UsersManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                  ]}
                >
                  <Fallback dependsOn="data">
                    <UsersManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              index
            />
            <Route
              loader={VehiclesManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                  ]}
                >
                  <Fallback dependsOn="data">
                    <VehiclesManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="vehicles"
            />
            <Route
              loader={MaterialsManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                  ]}
                >
                  <Fallback dependsOn="data">
                    <MaterialsManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="materials"
            />
            <Route
              loader={TeamsManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                  ]}
                >
                  <Fallback dependsOn="data">
                    <TeamsManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="teams"
            />
            <Route
              loader={GroupsManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                >
                  <Fallback dependsOn="data">
                    <GroupsManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="groups"
            />
            <Route
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                >
                  <SegmentReviewSettingsPage />
                </RolesAuthRoute>
              }
              path="segment-review"
            />
            <Route
              loader={MachineryManagementLoader(userData)}
              element={
                <Fallback dependsOn="data">
                  <MachineryManagementPage />
                </Fallback>
              }
              path="auxiliar-machinery"
            />
            <Route
              loader={AnimalsManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                  ]}
                >
                  <Fallback dependsOn="data">
                    <AnimalsManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="animals"
            />
            <Route
              loader={ExternalCompaniesManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                >
                  <Fallback dependsOn="data">
                    <ExternalCompaniesManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="external-companies"
            />
            {/* Plantillas de recorrido (2026-08-16): mismos roles que Empresas
                externas — otro catálogo maestro del que vive el asistente. */}
            <Route
              loader={RoutePresetsManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                >
                  <Fallback dependsOn="data">
                    <RoutePresetsManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="route-presets"
            />
            <Route
              loader={BudgetCategoriesManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.RoomTeam]}
                >
                  <Fallback dependsOn="data">
                    <BudgetCategoriesManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="budget-categories"
            />
            <Route
              loader={BudgetItemsManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.RoomTeam]}
                >
                  <Fallback dependsOn="data">
                    <BudgetItemsManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
              path="budget-items"
            />
          </Route>
          {/* Surveillance Route - Recorridos de Vigilancia (v2.1) */}
          <Route
            path="surveillance-route"
            element={
              <FeatureRoute
                feature="feature-surveillance-route"
                fallbackPath="/work-orders"
              >
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                    Role.Tablet,
                  ]}
                >
                  <SurveillanceRouteLayout />
                </RolesAuthRoute>
              </FeatureRoute>
            }
          >
            {/* Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — tab-bar del
                LISTADO del módulo (Vista general/Actuacions/Multimedia).
                `actions`/`multimedia-list` son rutas ESTÁTICAS hermanas de
                `:routeId` (como `new-route` más abajo) — React Router
                prioriza el segment estático sobre el dinámico, así que
                `:routeId` NUNCA las captura. Filtran por `containerTypes:
                ['surveillanceRoute']` (TIPO de origen), no por un RV
                concreto — reemplaza el diseño equivocado de 8e433e04
                (revertido en esta misma tanda), que ponía estas 2 pestañas
                DENTRO del detalle de cada RV. El detalle
                (`RouteDetailedPage` más abajo) conserva sus 2 tabs de
                siempre, sin tocar. */}
            <Route
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                    Role.Tablet,
                  ]}
                >
                  <GeneralViewRoutePage />
                </RolesAuthRoute>
              }
            >
              <Route
                index
                loader={LoaderSurveillanceRoutesGeneralView(userData)}
                element={
                  <Fallback dependsOn="data">
                    <SurveillanceRoutesPage />
                  </Fallback>
                }
              />
              <Route
                path="actions"
                element={
                  <Fallback dependsOn="data">
                    <RouteActionsTab />
                  </Fallback>
                }
                loader={makeRouteActionsLoader({
                  containerTypes: ['surveillanceRoute'],
                  tableId: tableIds.RV_ACTIONS,
                  // surveillance_default_team: filtro Equipo prellenado con
                  // el equipo de vigilancia por defecto del tenant (una vez
                  // por sesión; el usuario puede quitarlo).
                  defaultTeamFromTenantConfig: true,
                })(userData)}
              />
              <Route
                path="multimedia-list"
                element={
                  <Fallback dependsOn="data">
                    <RouteMultimediaTab />
                  </Fallback>
                }
                loader={makeRouteMultimediaLoader({
                  containerTypes: ['surveillanceRoute'],
                  tableId: tableIds.RV_MULTIMEDIA,
                })(userData)}
              />
            </Route>
            <Route
              path="new-route"
              loader={LoaderNewSurveillanceRoute(userData)}
              action={ActionNewRoute}
              element={
                // D11 (fix 2026-07-04): Tablet SÍ crea RVs; RoomTeam NO.
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.Tablet,
                  ]}
                >
                  <Fallback dependsOn="data">
                    <NewRoutePage />
                  </Fallback>
                </RolesAuthRoute>
              }
            />
            {/* Reorg históricos→Informes (2026-07-04): la vista vive en
                /reporting; se conserva el path viejo como redirect, igual que
                el resto de históricos (PR #61). */}
            <Route
              path="search-create-historical-route"
              element={
                <Navigate
                  to="/reporting/search-create-historical-route"
                  replace
                />
              }
            />
            <Route path=":routeId">
              {/* Wizard EditRoute (F4 2026-07-17) — hermano del detalle, sin
                  su barra de tabs (patrón EditCampaign, standalone). Reusa el
                  wizard de NewRoute en modo edición. RBAC ruta espejo del
                  canónico CON Tablet + Capataz (spec §2.4); el gating por
                  ESTADO lo aplica el backend (barrera 2) y los botones de
                  entrada de F5. */}
              <Route
                path="edit"
                loader={LoaderEditRoute(userData)}
                action={ActionEditRoute}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <EditRoutePage />
                    </Fallback>
                  </RolesAuthRoute>
                }
              />
              <Route
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <RouteDetailedPage />
                  </RolesAuthRoute>
                }
              >
                <Route
                  index
                  loader={LoaderRouteDetailed(userData)}
                  element={
                    <Fallback dependsOn="data">
                      <RoutePreviewPage />
                    </Fallback>
                  }
                />
                <Route
                  path="child-orders"
                  loader={LoaderRouteChildOrders(userData)}
                  // D3 (2026-07-20): la card gemela del RV pasa a read-only
                  // total — su lápiz navega al wizard EditRoute en vez de
                  // togglear edición inline. Sin submit, no hace falta
                  // `action` en esta ruta (antes `ActionRouteChildOrders` →
                  // updateSurveillanceRoute, retirado junto con el fichero).
                  element={
                    <Fallback dependsOn="data">
                      <RouteChildOrdersPage />
                    </Fallback>
                  }
                />
              </Route>
              {/* Multimedia standalone (2026-07-08): dejó de ser tab del
                  detalle — ahora es hermana de él, sin la barra de tabs,
                  espejo de `/work-orders/:id/multimedia`. Destino de "Ver
                  fotos" desde ShowOT en la tab "Órdenes asociadas" (antes
                  navegaba a `./multimedia`, relativo a `child-orders`, y
                  404-aba). */}
              <Route
                path="multimedia"
                loader={LoaderRouteMultimedia(userData)}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <RouteMultimediaPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
              />
              {/* Foto individual namespaced al RV (2026-07-08): hermana de la
                  lista de arriba, espejo de `/work-orders/multimedia/:id`
                  (misma page + loader). Antes el double-tap de fila y el
                  botón "Ver fotos" de `RouteMultimediaPage` saltaban siempre
                  a `/work-orders/multimedia/:id` — la ruta plana bajo
                  work-orders — y el shell (sidebar + breadcrumb) cambiaba de
                  "Recorregut de Vigilància" a "Órdenes de Trabajo". Con esta
                  ruta hermana bajo `surveillance-route/:routeId`, el mismo
                  componente/loader renderiza dentro del shell del RV. */}
              <Route
                loader={LoaderGeneralViewMultimediaGrid(userData)}
                path="multimedia/:id"
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                      Role.Tablet,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <MultimediaGridGeneralView />
                    </Fallback>
                  </RolesAuthRoute>
                }
              />
              {/* Crear OT dentro de un tram — reutiliza NewWorkOrderForm + ContentCardResume */}
              <Route path={'new-order'}>
                <Route
                  index
                  loader={NewWorkOrderLoader(
                    userData,
                    formOrderNaming.NEW_SURVEILLANCE_ROUTE_ORDER_FORM
                  )}
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <Fallback dependsOn="data">
                        <NewSurveillanceRouteOrderPage />
                      </Fallback>
                    </RolesAuthRoute>
                  }
                />
                <Route
                  path="summary"
                  action={ActionNewSurveillanceRouteOrderSummary}
                  element={
                    <RolesAuthRoute
                      roles={[
                        Role.SuperAdmin,
                        Role.Coex,
                        Role.Capataz,
                        Role.RoomTeam,
                        Role.Tablet,
                      ]}
                    >
                      <SurveillanceRouteOrderSummary />
                    </RolesAuthRoute>
                  }
                />
              </Route>
              {/*
                RBAC de sub-rutas dentro del recorrido de vigilancia.

                Reglas canonicas (ver `src/utils/rolePermissions.ts#canCreateContentInState`
                y `permissions.constants.ts` en back):

                - Tablet: puede crear ACTUACIONES y ACCIDENTES dentro de un OT hijo.
                  No puede crear comunicaciones.
                - RoomTeam: puede crear COMUNICACIONES. No puede crear actuaciones
                  ni accidentes (igual que en workOrders y campaign).
                - Coex / SuperAdmin: acceso completo.
                - OperatingTeam: rol nominal (sin login real). No se lista aqui
                  aunque el backend lo acepte en el decorator `@Roles` del resolver,
                  porque en la UI no hay flujo real para el.
                - El sub-step `budget-items` dentro de new-action esta restringido
                  a SuperAdmin / Coex, igual que en workOrders.
              */}
              <Route
                path={'new-action'}
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.Tablet,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <NewActionPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={NewActionLoader(userData)}
                action={ActionNewAction}
              />
              <Route path="new-action">
                <Route
                  path={'details'}
                  element={
                    <EntityScreen entityKey={readCreationDraftId}>
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <ActionPage />
                        </Fallback>
                      </RolesAuthRoute>
                    </EntityScreen>
                  }
                  loader={LoaderActionPageOrder(userData)}
                >
                  {/*
                    Bloque del RECORRIDO: aquí solo se crean/editan actuaciones
                    BÁSICAS, y en ellas Operarios y Maquinaria están ocultas
                    (FR-9.3: la tripulación es del recorrido, no suya). El
                    índice apuntaba a `operators-and-vehicles`, así que
                    cualquier navegación sin pestaña —«Editar» desde el
                    resumen, un deep-link, un F5— aterrizaba en una pantalla
                    que la básica no tiene, y aquí NO hay rebote como sí lo hay
                    en la ruta de validación. La primera pestaña real de una
                    básica es Ubicación (fix 2026-08-05).
                  */}
                  {/* `index` y `path` son EXCLUYENTES: estaban en el mismo <Route> y
                      ganaba el `index`, así que el `path="*"` se ignoraba y el rescate
                      NUNCA funcionó — una pestaña inexistente daba «No routes matched
                      location» y pantalla de error en vez de llevar a la primera.

                      🔴 El destino del catch-all lleva `../` y el del índice no. No es
                      un descuido: es lo que manda la guía de React Router al partir una
                      ruta splat («include the extra `..` relative segment»). Desde el
                      `*` hay un nivel de ruta MÁS que desde el índice, y sin ese `..`
                      el destino se resuelve DENTRO del propio splat: vuelve a no casar,
                      el `*` recaptura y la URL crece sin fin. Verificado: bucle infinito.

                      `..` sube un nivel de RUTA, no un segmento de URL, así que aguanta
                      aunque el splat capture varios segmentos. */}
                  <Route index element={<Navigate to="location" replace />} />
                  <Route path="*" element={<Navigate to="../location" replace />} />
                  <Route
                    path="operators-and-vehicles"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <OperatorsAndVehiclesFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={OperatorsAndVehiclesLoader(userData)}
                  />
                  <Route
                    path="location"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <LocationFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={LocationLoader}
                  />
                  <Route
                    path="edition"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <EditionActionFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={BuildingLoader(userData)}
                  />
                  <Route
                    path="budget-items"
                    element={
                      // 2026-08-07: era la ÚNICA ruta de partidas que admitía a
                      // un rol ciego. Una sola regla, sin excepciones: las
                      // rutas de partidas rechazan a los roles ciegos siempre.
                      // Nadie llega aquí navegando — Edición desvía a
                      // Materiales con `roleCanViewBudgets`.
                      <RolesAuthRoute roles={[Role.SuperAdmin, Role.Coex]}>
                        <Fallback dependsOn="data">
                          <BudgetItemsFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={BudgetItemsLoader}
                  />
                  {/*
                    V4 FR-9.3: Machinery oculta (operaris/vehicles heredats del
                    RV).

                    2026-08-11 — la Actuació bàsica tampoco registra fauna, así
                    que MATERIALS es su paso terminal: lleva el mismo `action`
                    que Animals en la actuación normal (validación centralizada
                    + guardado completo). La ruta `animals` de abajo se conserva
                    viva a propósito, para que un enlace antiguo rebote en vez
                    de dar un 404 mudo.
                  */}
                  <Route
                    path="materials"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <MaterialsFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={MaterialsLoader}
                    action={AnimalsAction}
                  />
                  <Route
                    path="animals"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <AnimalsFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={AnimalsLoader()}
                    action={AnimalsAction}
                  />
                </Route>
                <Route
                  path={'summary/:id'}
                  element={
                    <EntityScreen>
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.Tablet,
                        ]}
                      >
                        {/* `key` propia (QA S3 / T3.4): sin ella React REUTILIZA el
                            `Fallback` del paso anterior del asistente (mismos tipos en la
                            misma posición) y, dentro de la transición de la navegación,
                            no cambia por el cargando un `Suspense` que ya enseñaba algo:
                            esperaba a los datos del resumen con el velo encima. Con un
                            `Suspense` nuevo, el velo se va y el cargando sale aquí. */}
                        <Fallback key="sr-basic-action-summary" dependsOn="data">
                          <ActionSummaryPage />
                        </Fallback>
                      </RolesAuthRoute>
                    </EntityScreen>
                  }
                  loader={ActionSummaryLoader}
                />
              </Route>
              {/*
                Ver/validar una actuación SIN salir del recorrido (2026-08-15).

                Crearla ya se quedaba dentro (`new-action` de arriba), pero
                verla saltaba a `/work-orders/actions-validation/...`: migas de
                Órdenes de trabajo, y para volver al recorrido solo el botón
                atrás. Este bloque es el TERCER árbol de `actions-validation`,
                espejo del que ya cuelga de `campaign` — mismos elementos,
                mismos loaders y **los mismos roles que el árbol global**, que
                es por donde pasaban estas actuaciones hasta hoy: relocalizar
                una pantalla no puede cambiar quién entra en ella.

                No lleva ruta índice (el listado de validación no vive aquí; el
                módulo tiene su propia pestaña `actions`), así que solo se
                entra por un id concreto.

                Deuda anotada: `budget-items` admite aquí a Tablet porque lo
                admite el árbol global, mientras que el `budget-items` del
                asistente de creación del recorrido ya solo deja pasar a
                SuperAdmin/Coex. La divergencia es PREEXISTENTE — se hereda tal
                cual para no cambiar permisos de tapadillo; unificarla es
                trabajo del hilo de ceguera a partidas.
              */}
              <Route path="actions-validation">
                <Route
                  path="summary/:id"
                  element={
                    <EntityScreen>
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <ActionSummaryPage />
                        </Fallback>
                      </RolesAuthRoute>
                    </EntityScreen>
                  }
                  loader={ActionSummaryLoader}
                />
                <Route
                  path=":id"
                  element={
                    <EntityScreen>
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <ActionValidationPage />
                        </Fallback>
                      </RolesAuthRoute>
                    </EntityScreen>
                  }
                  loader={LoaderActionValidationPageOrder}
                >
                  {/* `index` y `path` son EXCLUYENTES: estaban en el mismo <Route> y
                      ganaba el `index`, así que el `path="*"` se ignoraba y el rescate
                      NUNCA funcionó — una pestaña inexistente daba «No routes matched
                      location» y pantalla de error en vez de llevar a la primera.

                      🔴 El destino del catch-all lleva `../` y el del índice no. No es
                      un descuido: es lo que manda la guía de React Router al partir una
                      ruta splat («include the extra `..` relative segment»). Desde el
                      `*` hay un nivel de ruta MÁS que desde el índice, y sin ese `..`
                      el destino se resuelve DENTRO del propio splat: vuelve a no casar,
                      el `*` recaptura y la URL crece sin fin. Verificado: bucle infinito.

                      `..` sube un nivel de RUTA, no un segmento de URL, así que aguanta
                      aunque el splat capture varios segmentos. */}
                  <Route index element={<Navigate to="operators-and-vehicles" replace />} />
                  <Route path="*" element={<Navigate to="../operators-and-vehicles" replace />} />
                  <Route
                    path="operators-and-vehicles"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <OperatorsAndVehiclesFormValidationPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={OperatorsAndVehiclesValidationLoader(userData)}
                  />
                  <Route
                    path="location"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <LocationActionFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={LocationValidationLoader(userData)}
                  />
                  <Route
                    path="edition"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <EditionValidationFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={EditionValidationLoader(userData)}
                  />
                  <Route
                    path="budget-items"
                    element={
                      <RolesAuthRoute
                        roles={[Role.SuperAdmin, Role.Coex, Role.Tablet]}
                      >
                        <Fallback dependsOn="data">
                          <BudgetItemsValidationFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={budgetItemsValidationLoader}
                  />
                  <Route
                    path="materials"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <MaterialsValidationFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={MaterialsValidationLoader(userData)}
                    // Paso terminal de la Actuació bàsica (2026-08-11) — y en
                    // el recorrido TODAS lo son.
                    action={AnimalsValidationAction}
                  />
                  <Route
                    path="machinery"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <MachineryValidationFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={MachineryValidationLoader(userData)}
                  />
                  {/*
                    Maquinaria y fauna no las pinta una básica, pero sus rutas
                    se conservan por el mismo motivo que en el asistente de
                    creación: un enlace antiguo debe REBOTAR (lo hace
                    `ActionPage`, que manda `machinery`→Ubicación y
                    `animals`→Materiales), no dar un 404 mudo.
                  */}
                  <Route
                    path="animals"
                    element={
                      <RolesAuthRoute
                        roles={[
                          Role.SuperAdmin,
                          Role.Coex,
                          Role.Capataz,
                          Role.RoomTeam,
                          Role.Tablet,
                        ]}
                      >
                        <Fallback dependsOn="data">
                          <AnimalsValidationFormPage />
                        </Fallback>
                      </RolesAuthRoute>
                    }
                    loader={AnimalsValidationLoader(userData)}
                    action={AnimalsValidationAction}
                  />
                </Route>
              </Route>
              {/*
                Timer iteration 2026-04-15: new-accident and new-communication
                are no longer reachable from inside a surveillance route. The
                only sub-flow is "new-order" + nested "new-action". External
                accidents/communications are linked to the route via
                cross-link from their own creation forms.
              */}
            </Route>
          </Route>
          <Route
            path="reporting"
            element={
              <RolesAuthRoute
                roles={[
                  Role.SuperAdmin,
                  Role.Coex,
                  Role.Capataz,
                  Role.RoomTeam,
                ]}
              >
                <ReportingLayout />
              </RolesAuthRoute>
            }
          >
            <Route index element={<ReportingIndexRedirect />} />
            <Route
              path="time-management"
              loader={TimeManagementLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                >
                  <Fallback dependsOn="data">
                    <TimeManagementPage />
                  </Fallback>
                </RolesAuthRoute>
              }
            />
            <Route
              path="search-create-historical-route"
              loader={LoaderSearchAndCreateHistoricalRoute(userData)}
              element={
                // D11/FR-2.7: históricos de RV para supervisores (Tablet no).
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                  ]}
                >
                  <Fallback dependsOn="data">
                    <SearchAndCreateHistoricalRoutePage />
                  </Fallback>
                </RolesAuthRoute>
              }
            />
            <Route
              path="historical-actions"
              loader={HistoricalActionsReportLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                >
                  <Fallback dependsOn="data">
                    <HistoricalActionsReportPage />
                  </Fallback>
                </RolesAuthRoute>
              }
            />
            <Route
              path="historical-accidents"
              loader={HistoricalAccidentsReportLoader(userData)}
              element={
                <RolesAuthRoute
                  roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                >
                  <Fallback dependsOn="data">
                    <HistoricalAccidentsReportPage />
                  </Fallback>
                </RolesAuthRoute>
              }
            />
            {/* Históricos movidos a Informes (antes en Work Orders / Comunicaciones / Budgets) */}
            <Route path={'historical-generic-orders'}>
              <Route
                index
                element={
                  <RolesAuthRoute
                    roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                  >
                    <Fallback dependsOn="data">
                      <SearchAndCreateHistoricalOrderPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={SearchAndCreateHistoricalWorkOrdersLoader(userData)}
              />
              <Route
                path={'summary'}
                element={
                  <RolesAuthRoute
                    roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                  >
                    <SearchAndCreateHistoryWorkOrderSummary />
                  </RolesAuthRoute>
                }
              />
              <Route
                path={'summary/:id'}
                element={
                  <RolesAuthRoute
                    roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                  >
                    <Fallback dependsOn="data">
                      <SearchAndCreateHistoryWorkOrderSummary />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={LoaderSearchAndCreateHistoricalWorkOrderSummary}
              />
            </Route>
            <Route path={'historical-campaigns'}>
              <Route
                index
                element={
                  <RolesAuthRoute
                    roles={[Role.SuperAdmin, Role.Coex, Role.Capataz]}
                  >
                    <Fallback dependsOn="data">
                      <SearchAndCreateHistoricalCampaignPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
                loader={LoaderCampaigns(
                  userData,
                  tableIds.SEARCH_AND_CREATE_HISTORICAL_CAMPAIGNS
                )}
              />
            </Route>
            <Route
              path={'historical-communications'}
              element={
                <RolesAuthRoute
                  roles={[
                    Role.SuperAdmin,
                    Role.Coex,
                    Role.Capataz,
                    Role.RoomTeam,
                  ]}
                >
                  <LayoutResetIndex resetType={layoutTabReset.ALL} />
                </RolesAuthRoute>
              }
            >
              <Route
                loader={HistoricalCommunicationsLoader(userData)}
                index
                element={
                  <RolesAuthRoute
                    roles={[
                      Role.SuperAdmin,
                      Role.Coex,
                      Role.Capataz,
                      Role.RoomTeam,
                    ]}
                  >
                    <Fallback dependsOn="data">
                      <HistoricalCommunicationsReportPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
              />
              {/* 2026-08-04: aquí colgaban `edit-communication/:id` (+ su
                  `summary`) y `summary`/`summary/:id`. Un formulario de
                  escritura y un detalle no pintan en Informes: la edición vive
                  en `/work-orders/communications/edit-communication/:id` (ruta
                  que ya existía) y "Ver comunicación" salta al detalle del
                  módulo, igual que ya hacía "Ver Recorrido" del histórico del
                  RV. Informes queda como Actuaciones y Accidentes: filtrar y
                  descargar. */}
            </Route>
            <Route
              path={'historical-budgets'}
              element={
                <RolesAuthRoute roles={[Role.SuperAdmin, Role.Coex]}>
                  <LayoutResetIndex resetType={layoutTabReset.ALL} />
                </RolesAuthRoute>
              }
            >
              <Route
                loader={SearchAndCreateHistoricalBudgetLoader(userData)}
                index
                element={
                  <RolesAuthRoute roles={[Role.SuperAdmin, Role.Coex]}>
                    <Fallback dependsOn="data">
                      <SearchAndCreateHistoricalBudgetPage />
                    </Fallback>
                  </RolesAuthRoute>
                }
              />
              <Route
                element={
                  <RolesAuthRoute roles={[Role.SuperAdmin, Role.Coex]}>
                    <BudgetItemSummary />
                  </RolesAuthRoute>
                }
                path={'summary'}
              />
              <Route
                loader={LoaderBudgetItemSummary}
                element={
                  <RolesAuthRoute roles={[Role.SuperAdmin, Role.Coex]}>
                    <Fallback dependsOn="data">
                      <BudgetItemSummary />
                    </Fallback>
                  </RolesAuthRoute>
                }
                path={'summary/:id'}
              />
            </Route>
          </Route>
          <Route
            path="admin"
            element={
              <RolesAuthRoute roles={[Role.SuperAdmin]}>
                <AdminRoutesLayout />
              </RolesAuthRoute>
            }
          >
            <Route
              index
              loader={TenantsAdminLoader(userData)}
              element={
                <RolesAuthRoute roles={[Role.SuperAdmin]}>
                  <Fallback dependsOn="data">
                    <TenantsAdminPage />
                  </Fallback>
                </RolesAuthRoute>
              }
            />
            <Route
              path="tenants/:tenantId"
              loader={TenantDetailLoader(userData)}
              element={
                <RolesAuthRoute roles={[Role.SuperAdmin]}>
                  <Fallback dependsOn="data">
                    <TenantDetailPage />
                  </Fallback>
                </RolesAuthRoute>
              }
            />
          </Route>
          {/* 404 DENTRO de la aplicación. Va el ÚLTIMO del bloque a
              propósito: React Router ordena por especificidad, así que este
              comodín solo entra cuando ninguna ruta de arriba ha casado —los
              `path="*"` que rescatan una pestaña dentro de los asistentes
              siguen ganando en su propio subárbol.

              Existe para que una dirección equivocada NO expulse al operario
              de la aplicación: llega con la cabecera, el menú y su nombre
              arriba, que es lo que le dice que su sesión sigue viva. Sin
              sesión ni se pinta — `MainLayout` redirige al login antes.
              Es el patrón que recomiendan GOV.UK y compañía: la página de
              error vive dentro del marco normal del servicio. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
        );

    // ── Escalón 3: el splash del arranque en frío ─────────────────────────
    //
    // `v7_partialHydration` deprecia el `fallbackElement` del
    // <RouterProvider>, y su sustituto es un `HydrateFallback` de ruta: lo que
    // se pinta mientras resuelven los loaders de la carga inicial.
    //
    // 🔴 Y no se puede declarar en el JSX. `createRoutesFromElements` arma el
    // objeto de ruta con una LISTA FIJA de props (id, element, index, path,
    // loader, action, errorElement, shouldRevalidate, handle, lazy) en la que
    // `HydrateFallback` NO está: la prop se descarta en silencio. Lo comprobé
    // en el navegador — con `HydrateFallback` puesto en el <Route>, la consola
    // seguía diciendo «No HydrateFallback element provided». Quien sí lo
    // entiende es `mapRouteProperties`, que lo convierte en
    // `hydrateFallbackElement`, pero opera sobre el OBJETO de ruta.
    //
    // De ahí esta línea: se inyecta en la raíz ya construida. Cubre cualquier
    // ruta de entrada, porque la raíz está en todas las cadenas de match.
    //
    // Sin esto el flag no da ningún error — solo dos avisos en consola y el
    // splash desaparece, devolviendo la pantalla en blanco de 10-15 s del
    // arranque en frío.
    routes[0].HydrateFallback = SplashScreen;

    // En React Router 7 las seis future flags SON el comportamiento por
    // defecto, así que el bloque `future` que las activaba una a una durante
    // la migración ya no pinta nada y se retira. Lo que cada una implicaba, y
    // cómo se comprobó, está en los commits de los escalones 1 a 4 y en
    // `docs/superpowers/specs/2026-08-19-react-router-v7-design.md`.
    routerSingleton = sentryCreateBrowserRouter(routes);
  }

  router = routerSingleton;

  return (
    // Ya no lleva `fallbackElement`: con `v7_partialHydration` está deprecado
    // y su papel lo hace el `HydrateFallback` de la ruta raíz. El splash sigue
    // saliendo, además, desde `PersistGate.loading` (App.tsx) y desde el
    // proveedor de Apollo (gql/client.tsx) — este era el tercero de los tres.
    <RouterProvider router={router} />
  );
}
